import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as yup from 'yup';

import BaseErrors from '@components/fields/BaseErrors';
import BooleanRadioButtonGroup from '@components/fields/BooleanRadioButtonGroup';
import CheckboxGroup from '@components/fields/CheckboxGroup';
import DataSelect from '@components/fields/DataSelect';
import FormButtons from '@components/fields/FormButtons';
import Input from '@components/fields/Input';
import LocationAutocompleteInput from '@components/fields/LocationAutocompleteInput';
import styles from '@components/fields/RadioButtonGroupWithIcon.module.css';
import Form from '@components/forms/Form';
import {
  CareProfileFragment as CareProfile,
  CareProfileConcernEnum,
  CareProfileNeedsFromGrayceEnum,
  InitialConsultReportFragment,
  InitialConsultReportUpdateDocument,
  InitialConsultReportUpdateMutation,
  InitialConsultReportUpdateMutationVariables
} from '@typing/Generated';
import { InitialConsultFields } from '@typing/Models';
import { handleMutationResponse } from '@utils/formUtils';
import { optionsForBoolean } from '@utils/optionUtils';

type Client = InitialConsultReportFragment['journey']['owner'];

type FormData = {
  clientCulturalConsiderations: string | null;
  clientIsVeteran: boolean | null;
  clientPlaceId: string | null;
  clientTimezone: string | null;
  concerns: CareProfileConcernEnum[];
  needsFromGrayce: CareProfileNeedsFromGrayceEnum[];
};

type Props = {
  careProfile: CareProfile;
  client: Client;
  consultId: string | undefined | null;
  initialConsultReportId: string;
  onCancel?: () => void;
  onSuccess: (nextPage?: string) => void;
  questions?: InitialConsultFields[];
};

const InitialConsultClientInfoForm = ({
  careProfile,
  client,
  consultId,
  initialConsultReportId,
  onCancel,
  onSuccess,
  questions
}: Props) => {
  const initialValues: FormData = {
    clientCulturalConsiderations: client.culturalConsiderations ?? null,
    clientIsVeteran: client.isVeteran !== null ? client.isVeteran : null,
    clientPlaceId: import.meta.env.VITE_NO_GOOGLE_LOCATION_CALLS
      ? 'ChIJfX6-HwSbf4gR4Aa9pkeWBLY'
      : client.location.placeId,
    clientTimezone: client.timezone ?? null,
    concerns: careProfile.concerns,
    needsFromGrayce: careProfile.needsFromGrayce ?? []
  };

  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [updateInitialConsultReport] = useMutation<
    InitialConsultReportUpdateMutation,
    InitialConsultReportUpdateMutationVariables
  >(InitialConsultReportUpdateDocument, {
    update: (store, { data: data }) => {
      if (data?.initialConsultReportUpdate?.initialConsultReport) {
        store.modify({
          fields: {
            culturalConsiderations() {
              return (
                data.initialConsultReportUpdate?.initialConsultReport?.journey.owner.culturalConsiderations ?? null
              );
            },
            isVeteran() {
              return data.initialConsultReportUpdate?.initialConsultReport?.journey.owner.isVeteran ?? null;
            },
            location() {
              return data.initialConsultReportUpdate?.initialConsultReport?.journey.owner.location ?? null;
            },
            timezone() {
              return data.initialConsultReportUpdate?.initialConsultReport?.journey.owner.timezone ?? null;
            }
          },
          id: store.identify({ ...client })
        });
      }
    }
  });

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      clientCulturalConsiderations: yup.string().nullable().defined(),
      clientIsVeteran: yup.boolean().nullable().defined(),
      clientPlaceId: yup.string().nullable().defined(),
      clientTimezone: yup.string().nullable().defined(),
      concerns: yup
        .array()
        .defined()
        .of(yup.string().oneOf(Object.values(CareProfileConcernEnum)).defined()),
      needsFromGrayce: yup
        .array()
        .defined()
        .of(yup.string().oneOf(Object.values(CareProfileNeedsFromGrayceEnum)).defined())
    })
    .required();

  const form = useForm<FormData>({
    defaultValues: initialValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);
    return updateInitialConsultReport({
      variables: {
        ...form.getValues(),
        consultId,
        id: initialConsultReportId
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.initialConsultReportUpdate,
        intl,
        onSuccess: () => {
          if (!questions) {
            onSuccess('basic-info');
          } else {
            onSuccess();
          }
        },
        setBaseErrors,
        setError: form.setError,
        txBase: 'errors.careProfile'
      });
    });
  }, [updateInitialConsultReport, form, consultId, initialConsultReportId, intl, questions, onSuccess]);

  const booleanOptions = useMemo(() => optionsForBoolean(intl), [intl]);

  const needsFromGrayceOptions = useMemo(() => {
    const options = [
      CareProfileNeedsFromGrayceEnum.planning,
      CareProfileNeedsFromGrayceEnum.complexity_of_support,
      CareProfileNeedsFromGrayceEnum.advice_or_resources,
      CareProfileNeedsFromGrayceEnum.navigating_transition,
      CareProfileNeedsFromGrayceEnum.crisis,
      CareProfileNeedsFromGrayceEnum.end_of_life,
      CareProfileNeedsFromGrayceEnum.fatigue,
      CareProfileNeedsFromGrayceEnum.just_browsing
    ];
    return options.map(option => ({
      label: intl.formatMessage({ id: `models.careProfile.needsFromGrayce.${option}` }),
      value: option
    }));
  }, [intl]);

  const concernOptions = useMemo(
    () =>
      Object.values(CareProfileConcernEnum).map(option => ({
        label: intl.formatMessage({ id: `models.careProfile.questions.concerns.options.${option}.content` }),
        value: option
      })),
    [intl]
  );

  return (
    <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <div className={!questions || questions?.includes(InitialConsultFields.CLIENT_LOCATION) ? undefined : 'ion-hide'}>
        <LocationAutocompleteInput
          control={form.control}
          error={form.formState.errors.clientPlaceId}
          label={intl.formatMessage({ id: 'models.initialConsultReport.questions.client_location.content' })}
          location={{ address: client.location.address, placeId: client.location.placeId }}
          name="clientPlaceId"
          required
          subLabel={intl.formatMessage({ id: 'forms.careProfile.locationSublabel' })}
        />
      </div>
      <div className={!questions || questions?.includes(InitialConsultFields.CLIENT_TIMEZONE) ? undefined : 'ion-hide'}>
        <DataSelect
          control={form.control}
          error={form.formState.errors.clientTimezone}
          label={intl.formatMessage({ id: 'models.initialConsultReport.questions.client_timezone.content' })}
          name="clientTimezone"
          type="timezones"
        />
      </div>
      <div
        className={!questions || questions?.includes(InitialConsultFields.CLIENT_IS_VETERAN) ? undefined : 'ion-hide'}
        data-test="veteran"
      >
        <BooleanRadioButtonGroup
          allowDeselection
          control={form.control}
          error={form.formState.errors.clientIsVeteran}
          inTracker={false}
          label={intl.formatMessage({ id: 'models.initialConsultReport.questions.client_is_veteran.content' })}
          name="clientIsVeteran"
          options={booleanOptions}
        />
      </div>
      <div
        className={
          !questions || questions?.includes(InitialConsultFields.CLIENT_CULTURAL_CONSIDERATIONS)
            ? undefined
            : 'ion-hide'
        }
      >
        <Input
          control={form.control}
          error={form.formState.errors.clientCulturalConsiderations}
          label={intl.formatMessage({
            id: 'models.initialConsultReport.questions.client_cultural_considerations.content'
          })}
          name="clientCulturalConsiderations"
          spellcheck
        />
      </div>
      <div className={!questions ? undefined : 'ion-hide'}>
        <CheckboxGroup<FormData>
          control={form.control}
          errors={form.formState.errors.needsFromGrayce}
          label={intl.formatMessage({ id: 'forms.careProfile.needsFromGrayceLabel' })}
          name="needsFromGrayce"
          options={needsFromGrayceOptions}
        />
        <CheckboxGroup<FormData>
          control={form.control}
          errors={form.formState.errors.concerns}
          label={intl.formatMessage({ id: 'forms.careProfile.concernsLabel' })}
          name="concerns"
          options={concernOptions}
        />
      </div>
      <div className={questions ? undefined : styles.center} data-test="client-info-buttons">
        <FormButtons
          hookForm={form}
          onCancel={onCancel}
          saveKey={!questions ? 'dictionary.next' : undefined}
          skipCancel={questions === undefined}
        />
      </div>
    </Form>
  );
};

export default InitialConsultClientInfoForm;
